import { Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RiskGauge } from "@/components/executive/risk-gauge";
import { TrendCard } from "@/components/executive/trend-card";
import { AiNarrative } from "@/components/executive/ai-narrative";
import { SeverityDonut } from "@/components/executive/severity-donut";
import { TopRulesBar } from "@/components/executive/top-rules-bar";
import { InsuranceChecklist } from "@/components/executive/insurance-checklist";
import { useExecutiveSummary, useExecutiveExport } from "@/hooks/use-api";

export default function ExecutivePage() {
  const { data, isLoading, isError } = useExecutiveSummary();
  const exportMutation = useExecutiveExport();

  const handleExport = async () => {
    try {
      const result = await exportMutation.mutateAsync();
      // Client-side: download JSON for PDF generation
      const blob = new Blob([JSON.stringify(result.export, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive-brief-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // mutation error state handles display
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Executive Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            Security posture overview for leadership and board reporting
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exportMutation.isPending}
          variant="outline"
          className="gap-2"
        >
          {exportMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export Brief
        </Button>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load executive dashboard. Data will retry automatically.
        </div>
      )}

      {/* Risk Score — center hero */}
      <div className="flex justify-center">
        {isLoading ? (
          <Skeleton className="h-56 w-48" />
        ) : data ? (
          <RiskGauge score={data.risk_score} label={data.risk_label} />
        ) : null}
      </div>

      {/* Trend Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))
          : data?.trends.map((metric) => (
              <TrendCard key={metric.name} metric={metric} />
            ))}
      </div>

      {/* AI Narrative */}
      <AiNarrative
        narrative={data?.ai_narrative ?? null}
        isLoading={isLoading}
      />

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {isLoading ? (
          <>
            <Card><CardContent className="p-4"><Skeleton className="h-48 w-full" /></CardContent></Card>
            <Card><CardContent className="p-4"><Skeleton className="h-48 w-full" /></CardContent></Card>
          </>
        ) : data ? (
          <>
            <SeverityDonut data={data.severity_distribution} />
            <TopRulesBar rules={data.top_rules} />
          </>
        ) : null}
      </div>

      {/* Insurance Readiness */}
      {isLoading ? (
        <Card><CardContent className="p-4"><Skeleton className="h-48 w-full" /></CardContent></Card>
      ) : data ? (
        <InsuranceChecklist items={data.insurance_readiness} />
      ) : null}
    </div>
  );
}
